import React, { FC, forwardRef, useRef, useState } from 'react';
import Input from 'react-phone-number-input/input';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';

import { IStyledTextInputProps } from '@ui/forms/TextInput';

import theme from '@theme/FolxTheme';

type IPhoneTextInputProps = IStyledTextInputProps;

const TextLabel = styled.label<{ fullWidth: boolean }>`
	display: flex;
	flex-flow: column;
	width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};

	${BasicText} {
		margin-bottom: ${({ theme: components }) => components.basicText.labelMarginBottom};
	}
`;

const ErrorBox = styled.span`
	color: ${({ theme: components }) => components.textInput.errorColor};
	font-size: ${({ theme: components }) => components.textInput.errorFontSize};
	line-height: ${({ theme: components }) => components.textInput.errorLineHeight};
	margin-top: 0.8rem;
`;

const PhoneTextInput: FC<IPhoneTextInputProps> = forwardRef(
	(
		{
			className,
			disabled = false,
			fullWidth = false,
			label = '',
			name = '',
			placeholder,
			onChange = () => {},
			onKeyUp = () => {},
			value,
			errorLabel,
			error,
		},
		ref
	) => {
		const input = (
			<Input
				className={className}
				defaultCountry="US"
				disabled={disabled}
				label={label}
				name={name}
				placeholder={placeholder}
				onChange={onChange}
				onKeyUp={onKeyUp}
				type="tel"
				value={value}
				ref={ref}
				aria-label={name}
				data-cy={name}
			/>
		);
		return label ? (
			<TextLabel htmlFor={name} fullWidth={fullWidth}>
				<BasicText variant="field">{label}</BasicText>
				{input}
				{error && errorLabel && <ErrorBox>{errorLabel}</ErrorBox>}
			</TextLabel>
		) : (
			input
		);
	}
);

PhoneTextInput.displayName = 'PhoneTextInput';
export default styled(PhoneTextInput)<IPhoneTextInputProps>`
	border: ${({ border = true, error }) =>
		border && !error ? `1px solid ${theme.colors.black}` : `1px solid ${theme.colors.transparent}`};
	font-family: Chalet-LondonSixty, sans-serif;
	font-size: ${({ theme: components }) => components.basicText.body.fontSize.mobile};
	padding: 2rem 1rem;
	outline: ${({ error }) =>
		error ? `1px solid ${theme.tints.darken40(theme.colors.red)}` : 'none'};
	width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};

	@media ${theme.responsive.breakpoints.tablet} {
		font-size: ${({ theme: components }) => components.basicText.body.fontSize.tablet};
	}

	&:focus {
		outline: ${({ borderColor = 'cyan', error }) =>
			`2px solid ${error ? theme.tints.darken40(theme.colors.red) : theme.colors[borderColor]}`};
		border: 1px solid ${theme.colors.transparent};
		border-radius: 0;
	}

	&::placeholder {
		color: ${theme.tints.lighten70(theme.colors.black)};
	}

	input {
		width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
	}
`;
